import React, { useState } from 'react';
import { X, Upload, FileText, DollarSign, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const DocumentUploadModal = ({ isOpen, onClose, customer, onSuccess }) => {
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [statementFile, setStatementFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        // 10MB limit
        toast.error('File size must be less than 10MB');
        return;
      }
      if (type === 'invoice') {
        setInvoiceFile(file);
      } else {
        setStatementFile(file);
      }
    }
  };

  const handleUpload = async () => {
    if (!invoiceFile && !statementFile) {
      toast.error('Please select at least one file to upload');
      return;
    }

    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_BACKEND_URL;
      const token = localStorage.getItem('customerAdminToken');

      const formData = new FormData();
      if (invoiceFile) formData.append('invoice', invoiceFile);
      if (statementFile) formData.append('statement', statementFile);
      formData.append('customerId', customer._id);

      const response = await fetch(`${backendUrl}/api/customer-admin/customers/${customer._id}/upload-documents`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: `Server error: ${response.status}` }));
        toast.error(errorData.detail || 'Failed to upload documents');
        return;
      }

      const data = await response.json();
      
      toast.success('Documents uploaded successfully!');
      onSuccess();
      onClose();
      
      // Reset
      setInvoiceFile(null);
      setStatementFile(null);
    } catch (error) {
      console.error('Error uploading documents:', error);
      toast.error('Failed to upload documents');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !customer) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 flex justify-between items-center rounded-t-2xl">
          <div>
            <h2 className="text-2xl font-bold">Upload Documents</h2>
            <p className="text-blue-100 mt-1">{customer.firstName} {customer.lastName}</p>
            <p className="text-xs text-blue-200">{customer.email}</p>
          </div>
          <button onClick={onClose} className="hover:bg-white/20 rounded-full p-2 transition">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              Upload invoice and account statement for this customer. These documents will be available in the customer's dashboard for viewing and download.
            </p>
          </div>

          {/* Invoice Upload */}
          <div className="space-y-3">
            <label className="block">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium text-gray-700">Invoice Document</span>
              </div>
              <div className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition ${
                invoiceFile ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
              }`}>
                {invoiceFile ? (
                  <div className="flex items-center justify-center gap-2 text-green-600">
                    <CheckCircle className="w-6 h-6" />
                    <span className="font-medium">{invoiceFile.name}</span>
                  </div>
                ) : (
                  <div className="text-gray-500">
                    <Upload className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm">Click to upload invoice</p>
                    <p className="text-xs text-gray-400 mt-1">PDF, PNG, JPG (Max 10MB)</p>
                  </div>
                )}
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={(e) => handleFileChange(e, 'invoice')}
                  className="hidden"
                />
              </div>
            </label>
          </div>

          {/* Statement Upload */}
          <div className="space-y-3">
            <label className="block">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-5 h-5 text-purple-600" />
                <span className="text-sm font-medium text-gray-700">Account Statement</span>
              </div>
              <div className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition ${
                statementFile ? 'border-purple-500 bg-purple-50' : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
              }`}>
                {statementFile ? (
                  <div className="flex items-center justify-center gap-2 text-purple-600">
                    <CheckCircle className="w-6 h-6" />
                    <span className="font-medium">{statementFile.name}</span>
                  </div>
                ) : (
                  <div className="text-gray-500">
                    <Upload className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm">Click to upload statement</p>
                    <p className="text-xs text-gray-400 mt-1">PDF, PNG, JPG (Max 10MB)</p>
                  </div>
                )}
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={(e) => handleFileChange(e, 'statement')}
                  className="hidden"
                />
              </div>
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-semibold"
            >
              Cancel
            </button>
            <button
              onClick={handleUpload}
              disabled={loading || (!invoiceFile && !statementFile)}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Uploading...' : 'Upload Documents'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadModal;
